/**
 * API Service Layer for Girne MUN 2026
 * Connected to real Node.js + Express + MongoDB backend.
 */

// Production backend URL - hardcoded for reliability
const PRODUCTION_BACKEND_URL = 'https://girnemun-2026.onrender.com';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || PRODUCTION_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token storage
const getToken = () => localStorage.getItem('admin_token');
const setToken = (token) => localStorage.setItem('admin_token', token);
const removeToken = () => localStorage.removeItem('admin_token');

// API helper with auth
const apiRequest = async (endpoint, options = {}) => {
    const token = getToken();
    
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API}${endpoint}`, {
        ...options,
        headers,
    });
    
    const data = await response.json();
    
    if (!response.ok) {
        throw new Error(data.message || 'API request failed');
    }
    
    return data;
};

/**
 * Application API Functions
 */

// Submit new application - using fetch with explicit POST
export const submitApplication = async (applicationData) => {
    const url = `${PRODUCTION_BACKEND_URL}/api/applications`;
    
    console.log('Submitting application to:', url);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
    });
    
    const data = await response.json();
    
    if (!response.ok || !data.success) {
        throw new Error(data.message || 'Başvuru gönderilemedi');
    }
    
    return data;
};

// Check if email already exists
export const checkEmailExists = async (email) => {
    try {
        const response = await apiRequest(`/applications/check-email?email=${encodeURIComponent(email)}`);
        return response.exists;
    } catch (error) {
        return false;
    }
};

/**
 * Admin API Functions
 */

// Admin login (direct - without OTP)
export const adminLogin = async (email, password) => {
    try {
        const response = await apiRequest('/admin/login-direct', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        
        if (response.success && response.data.token) {
            setToken(response.data.token);
        }
        
        return response;
    } catch (error) {
        throw error;
    }
};

// Admin login with OTP - Step 1
export const adminLoginOTP = async (email, password) => {
    try {
        const response = await apiRequest('/admin/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        return response;
    } catch (error) {
        throw error;
    }
};

// Admin verify OTP - Step 2
export const adminVerifyOTP = async (email, otp) => {
    try {
        const response = await apiRequest('/admin/verify-otp', {
            method: 'POST',
            body: JSON.stringify({ email, otp }),
        });
        
        if (response.success && response.data.token) {
            setToken(response.data.token);
        }
        
        return response;
    } catch (error) {
        throw error;
    }
};

// Admin logout
export const adminLogout = () => {
    removeToken();
};

// Check if admin is logged in
export const isAdminLoggedIn = () => {
    const token = getToken();
    if (!token) return false;
    
    try {
        // Check if token is expired
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 > Date.now();
    } catch {
        return false;
    }
};

// Get current admin info
export const getAdminInfo = async () => {
    try {
        const response = await apiRequest('/admin/me');
        return response.data.admin;
    } catch (error) {
        removeToken();
        throw error;
    }
};

// Get all applications with filters
export const getApplications = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        
        if (filters.applicationType) params.append('applicationType', filters.applicationType);
        if (filters.status) params.append('status', filters.status);
        if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
        if (filters.city) params.append('city', filters.city);
        if (filters.grade) params.append('grade', filters.grade);
        if (filters.search) params.append('search', filters.search);
        if (filters.page) params.append('page', filters.page);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        
        const queryString = params.toString();
        const endpoint = `/admin/applications${queryString ? `?${queryString}` : ''}`;
        
        const response = await apiRequest(endpoint);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get single application
export const getApplication = async (id) => {
    try {
        const response = await apiRequest(`/admin/applications/${id}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update application status
export const updateApplicationStatus = async (id, status) => {
    try {
        const response = await apiRequest(`/admin/applications/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update payment status
export const updatePaymentStatus = async (id, paymentStatus) => {
    try {
        const response = await apiRequest(`/admin/applications/${id}/payment`, {
            method: 'PATCH',
            body: JSON.stringify({ paymentStatus }),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update admin notes
export const updateAdminNotes = async (id, adminNotes) => {
    try {
        const response = await apiRequest(`/admin/applications/${id}/notes`, {
            method: 'PATCH',
            body: JSON.stringify({ adminNotes }),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Delete application (superadmin only)
export const deleteApplication = async (id) => {
    try {
        const response = await apiRequest(`/admin/applications/${id}`, {
            method: 'DELETE',
        });
        return response;
    } catch (error) {
        throw error;
    }
};

// Get dashboard statistics
export const getDashboardStats = async () => {
    try {
        const response = await apiRequest('/admin/stats');
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Committee Data (Static - kept on frontend)
 */
export const committees = [
    {
        id: 'sochum',
        name: 'SOCHUM',
        fullName: 'Social, Humanitarian & Cultural Committee',
        description: 'Addresses social, humanitarian, and cultural issues affecting the international community.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [
                { name: null, bio: null, photo: null }
            ],
            directors: [],
            academicAssistants: [
                { name: null, bio: null, photo: null }
            ]
        }
    },
    {
        id: 'ecosoc',
        name: 'ECOSOC',
        fullName: 'Economic and Social Council',
        description: 'Coordinates economic and social affairs and promotes sustainable development.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [],
            academicAssistants: [{ name: null, bio: null, photo: null }]
        }
    },
    {
        id: 'fia',
        name: 'FIA',
        fullName: 'Fédération Internationale de l\'Automobile',
        description: 'FIA is the governing body of world motor sport. In this high-speed crisis committee, your strategic decisions and leadership skills will determine your success. Every action shapes your reputation — do you have the courage to compete?',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [{ name: null, bio: null, photo: null }],
            academicAssistants: []
        }
    },
    {
        id: 'series',
        name: 'SERIES',
        fullName: 'Strategic Economic Research & International Exchange Summit',
        description: 'Explores strategic economic research and international exchange dynamics.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [],
            academicAssistants: [{ name: null, bio: null, photo: null }]
        }
    },
    {
        id: 'h-specpol',
        name: 'H-SPECPOL',
        fullName: 'Historical Special Political and Decolonization Committee',
        description: 'Examines historical political events and decolonization processes.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [],
            academicAssistants: [{ name: null, bio: null, photo: null }]
        }
    },
    {
        id: 'tbmm',
        name: 'TBMM',
        fullName: 'Türkiye Büyük Millet Meclisi',
        description: 'Simulates the Turkish Grand National Assembly, focusing on national and regional issues.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [],
            academicAssistants: [{ name: null, bio: null, photo: null }]
        }
    },
    {
        id: 'disec',
        name: 'DISEC',
        fullName: 'Disarmament and International Security Committee',
        description: 'Deals with disarmament, global challenges to peace and security.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [],
            academicAssistants: [{ name: null, bio: null, photo: null }]
        }
    },
    {
        id: 'oligarchy',
        name: 'Oligarchy',
        fullName: 'The Council of Oligarchs',
        description: 'A unique crisis committee exploring power dynamics and wealth distribution.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [{ name: null, bio: null, photo: null }],
            academicAssistants: []
        }
    },
    {
        id: 'ottoman-dynasty',
        name: 'Ottoman Dynasty',
        fullName: 'The Imperial Court of the Ottoman Empire',
        description: 'A historical crisis committee set in the Ottoman Empire, exploring imperial politics and succession.',
        agenda: 'Open Agenda',
        roleDistribution: {
            chairs: [{ name: null, bio: null, photo: null }],
            directors: [{ name: null, bio: null, photo: null }],
            academicAssistants: []
        }
    }
];

// Get committees
export const getCommittees = async () => {
    return committees;
};

// Get single committee
export const getCommittee = async (id) => {
    return committees.find(c => c.id === id) || null;
};

/**
 * Contact Form (can be connected to email service later)
 */
export const submitContactForm = async (formData) => {
    // TODO: Connect to nodemailer endpoint when email service is configured
    return { success: true, message: 'Mesajınız alındı. En kısa sürede size dönüş yapacağız.' };
};

export default {
    submitApplication,
    checkEmailExists,
    adminLogin,
    adminLoginOTP,
    adminVerifyOTP,
    adminLogout,
    isAdminLoggedIn,
    getAdminInfo,
    getApplications,
    getApplication,
    updateApplicationStatus,
    updatePaymentStatus,
    updateAdminNotes,
    deleteApplication,
    getDashboardStats,
    committees,
    getCommittees,
    getCommittee,
    submitContactForm,
};
